import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function TeamSetup() {
  const [players, setPlayers] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [playerNumber, setPlayerNumber] = useState('');
  const [teamName, setTeamName] = useState('');
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [originalTeamName, setOriginalTeamName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      const teamDoc = await getDoc(doc(db, 'teams', auth.currentUser.uid));
      if (teamDoc.exists()) {
        const data = teamDoc.data();
        setPlayers(data.players || []);
        setTeamName(data.teamName || '');
        setOriginalTeamName(data.teamName || '');
      }
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  };

  const saveTeamName = async () => {
    if (!teamName.trim()) {
      alert('Team name cannot be empty');
      setTeamName(originalTeamName);
      setEditingTeamName(false);
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'teams', auth.currentUser.uid), {
        teamName: teamName.trim()
      });
      setOriginalTeamName(teamName.trim());
      setEditingTeamName(false);
    } catch (error) {
      alert('Failed to update team name');
      setTeamName(originalTeamName);
    } finally {
      setLoading(false);
    }
  };

  const cancelTeamNameEdit = () => {
    setTeamName(originalTeamName);
    setEditingTeamName(false);
  };

  const addPlayer = async () => {
    if (!playerName.trim()) {
      alert('Please enter a player name');
      return;
    }

    const newPlayer = {
      id: Date.now().toString(),
      name: playerName.trim(),
      number: playerNumber.trim() || '',
    };

    const updatedPlayers = [...players, newPlayer];
    
    setLoading(true);
    try {
      await updateDoc(doc(db, 'teams', auth.currentUser.uid), {
        players: updatedPlayers
      });
      setPlayers(updatedPlayers);
      setPlayerName('');
      setPlayerNumber('');
    } catch (error) {
      alert('Failed to add player');
    } finally {
      setLoading(false);
    }
  };

  const removePlayer = async (playerId) => {
    if (!window.confirm('Remove this player?')) return;

    const updatedPlayers = players.filter(p => p.id !== playerId);
    try {
      await updateDoc(doc(db, 'teams', auth.currentUser.uid), {
        players: updatedPlayers
      });
      setPlayers(updatedPlayers);
    } catch (error) {
      alert('Failed to remove player');
    }
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Team Setup</h1>
        <button 
          onClick={() => navigate('/')}
          style={{
            background: 'white',
            border: '2px solid white',
            color: '#243665',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: '600',
            minWidth: '80px',
            minHeight: '44px',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          Back
        </button>
      </div>

      <div style={{ padding: '20px 0' }}>
        {/* Team Name Section */}
        <div className="card">
          <h3 style={{ marginBottom: '16px', color: '#243665' }}>Team Name</h3>
          
          {editingTeamName ? (
            <>
              <div>
                <label className="input-label">TEAM NAME *</label>
                <input
                  type="text"
                  className="input"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Team name"
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <button
                  className="button"
                  onClick={saveTeamName}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cancelTeamNameEdit}
                  disabled={loading}
                  style={{
                    flex: 1,
                    background: 'white',
                    border: '2px solid #243665',
                    color: '#243665',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    minHeight: '44px',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '12px 0'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#243665', fontSize: '18px' }}>
                  {teamName || 'No team name set'}
                </div>
              </div>
              <button
                onClick={() => setEditingTeamName(true)}
                style={{
                  background: 'white',
                  border: '2px solid #243665',
                  color: '#243665',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  minHeight: '44px',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                Edit
              </button>
            </div>
          )}
        </div>

        {/* Add Players Section */}
        <div className="card" style={{ marginTop: '20px' }}>
          <h3 style={{ marginBottom: '16px', color: '#243665' }}>Add Players</h3>
          
          <div>
            <label className="input-label">PLAYER NAME *</label>
            <input
              type="text"
              className="input"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Player name"
            />
          </div>

          <div>
            <label className="input-label">NUMBER (Optional)</label>
            <input
              type="text"
              className="input"
              value={playerNumber}
              onChange={(e) => setPlayerNumber(e.target.value)}
              placeholder="Jersey number"
            />
          </div>

          <button
            className="button"
            onClick={addPlayer}
            disabled={loading}
          >
            + Add Player
          </button>
        </div>

        {/* Players List */}
        <h3 style={{ margin: '24px 0 16px', color: '#243665' }}>
          Your Squad ({players.length})
        </h3>

        {players.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666' }}>No players added yet</p>
          </div>
        ) : (
          players.map((player) => (
            <div key={player.id} className="card" style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px'
            }}>
              <div>
                <div style={{ fontWeight: '600', color: '#243665' }}>
                  {player.name}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {player.number ? `#${player.number}` : 'No number'}
                </div>
              </div>
              <button
                onClick={() => removePlayer(player.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#d32f2f',
                  cursor: 'pointer',
                  fontSize: '20px',
                  minWidth: '44px',
                  minHeight: '44px',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default TeamSetup;
